import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';

import { flattenErrorKeys, findFieldElement, findFieldLabel, scrollToAndFocus } from './formErrors';
import css from './FormErrorSummary.module.css';

/**
 * "Du mangler ett felt: Tittel" above a form's proceed button.
 *
 * Shows only after the seller has tried to proceed (final-form's
 * `submitFailed`), lists the fields still blocking them with each name a
 * button that scrolls to and focuses that field, and disappears on its own
 * as fields are fixed — it's driven by the live `errors`, so a field drops
 * off when it is actually valid, not on the first keystroke. On every
 * submit attempt that fails (button click or Enter), the first blocking
 * field is scrolled into view and focused.
 *
 * @param {Object} props
 * @param {Object} props.errors - final-form `errors`
 * @param {boolean} props.submitFailed - final-form `submitFailed`
 * @param {React.RefObject} props.formRef - the <form> element, to find fields in
 * @param {string[]} [props.order] - field names in visual order (defaults to DOM order)
 * @param {string} [props.className]
 */
const FormErrorSummary = props => {
  const { errors, submitFailed, formRef, className } = props;
  const intl = useIntl();
  const keys = useMemo(() => flattenErrorKeys(errors), [errors]);
  const active = submitFailed && keys.length > 0;

  // Sort blocking fields by where they sit on the page, so "first" is what a
  // person would call first.
  const ordered = useMemo(() => {
    const root = formRef?.current;
    if (!root) {
      return keys;
    }
    const withPos = keys.map(name => {
      const el = findFieldElement(root, name);
      return { name, el, label: findFieldLabel(root, name) || name };
    });
    return withPos
      .sort((a, b) => {
        if (!a.el || !b.el) {
          return 0;
        }
        return a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
      })
      .map(({ name, label }) => ({ name, label }));
  }, [keys, formRef, active]);

  // Count submit attempts off the <form> itself: final-form's `submitFailed`
  // flips to true once and stays there, so it can't tell a second failed
  // click from the first. Every attempt that still fails re-points the
  // seller at the first blocking field.
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const formEl = formRef?.current;
    if (!formEl) {
      return undefined;
    }
    const onSubmit = () => setAttempt(n => n + 1);
    formEl.addEventListener('submit', onSubmit);
    return () => formEl.removeEventListener('submit', onSubmit);
  }, [formRef]);
  const lastHandled = useRef(0);
  useEffect(() => {
    if (!active || attempt === lastHandled.current) {
      return;
    }
    lastHandled.current = attempt;
    const first = ordered[0];
    if (first) {
      scrollToAndFocus(findFieldElement(formRef?.current, first.name));
    }
  }, [active, ordered, formRef, attempt]);

  if (!active) {
    return null;
  }

  const links = ordered.map(({ name, label }, index) => (
    <React.Fragment key={name}>
      {index > 0 ? ', ' : null}
      <button
        type="button"
        className={css.fieldLink}
        onClick={() => scrollToAndFocus(findFieldElement(formRef?.current, name))}
      >
        {label}
      </button>
    </React.Fragment>
  ));

  return (
    <p className={classNames(css.root, className)} role="alert">
      {intl.formatMessage(
        { id: 'FormErrorSummary.missing' },
        { count: ordered.length, fields: links }
      )}
    </p>
  );
};

export default FormErrorSummary;
